import type {
  SlashMenuActionItem,
  SlashMenuConfig,
  SlashMenuContext,
  SlashMenuItem,
  SlashMenuSubMenu,
} from './types';

export function isActionItem(item: SlashMenuItem): item is SlashMenuActionItem {
  return 'action' in item;
}

export function isSubMenuItem(item: SlashMenuItem): item is SlashMenuSubMenu {
  return 'subMenu' in item;
}

export function slashItemClassName({ name }: SlashMenuItem) {
  return name.split(' ').join('-').toLocaleLowerCase();
}

export function parseGroup(group: NonNullable<SlashMenuItem['group']>) {
  return [
    parseInt(group.split('_')[0]),
    group.split('_')[1].split('@')[0],
    parseInt(group.split('@')[1]),
  ] as const;
}

function itemCompareFn(a: SlashMenuItem, b: SlashMenuItem) {
  if (a.group === undefined && b.group === undefined) return 0;
  if (a.group === undefined) return -1;
  if (b.group === undefined) return 1;

  const [aGroupIndex, aGroupName, aItemIndex] = parseGroup(a.group);
  const [bGroupIndex, bGroupName, bItemIndex] = parseGroup(b.group);
  if (isNaN(aGroupIndex)) return -1;
  if (isNaN(bGroupIndex)) return 1;
  if (aGroupIndex < bGroupIndex) return -1;
  if (aGroupIndex > bGroupIndex) return 1;

  if (aGroupName !== bGroupName) return aGroupName.localeCompare(bGroupName);

  if (isNaN(aItemIndex)) return -1;
  if (isNaN(bItemIndex)) return 1;

  return aItemIndex - bItemIndex;
}

export function buildSlashMenuItems(
  items: SlashMenuItem[],
  context: SlashMenuContext,
  transform?: (item: SlashMenuItem) => SlashMenuItem
): SlashMenuItem[] {
  if (transform) items = items.map(transform);

  const result = items
    .filter(item => (item.when ? item.when(context) : true))
    .sort(itemCompareFn)
    .map(item => {
      if (isSubMenuItem(item)) {
        return {
          ...item,
          subMenu: buildSlashMenuItems(item.subMenu, context, transform),
        };
      } else {
        return { ...item };
      }
    });
  return result;
}

export function mergeSlashMenuConfigs(
  configs: Map<string, SlashMenuConfig>
): SlashMenuConfig {
  return {
    items: ctx =>
      Array.from(configs.values()).flatMap(({ items }) =>
        typeof items === 'function' ? items(ctx) : items
      ),
    disableWhen: ctx =>
      configs
        .values()
        .map(({ disableWhen }) => disableWhen?.(ctx) ?? false)
        .some(Boolean),
  };
}

// ---------------------------------------------------------------------------
// Slash menu favorites — user-pinned items shown at the top of the menu.
// Persisted in localStorage so favorites survive reloads and work offline.
// ---------------------------------------------------------------------------

const SLASH_MENU_FAVORITES_STORAGE_KEY = 'affine.slash-menu.favorites';

/**
 * Stable identifier for a slash menu item.
 *
 * `name` is human-readable but not guaranteed unique across configs
 * (different blocks may register items with the same name), so we also
 * include the `group` token which encodes both the group and the order.
 */
export function favoriteKey(item: SlashMenuItem): string {
  return `${item.name}::${item.group ?? ''}`;
}

function isBrowserStorageAvailable(): boolean {
  try {
    return typeof localStorage !== 'undefined';
  } catch {
    return false;
  }
}

export function getFavoriteSlashKeys(): Set<string> {
  if (!isBrowserStorageAvailable()) return new Set();
  try {
    const raw = localStorage.getItem(SLASH_MENU_FAVORITES_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return new Set();
    return new Set(parsed.filter((v): v is string => typeof v === 'string'));
  } catch {
    return new Set();
  }
}

function persistFavoriteSlashKeys(keys: Set<string>): void {
  if (!isBrowserStorageAvailable()) return;
  try {
    localStorage.setItem(
      SLASH_MENU_FAVORITES_STORAGE_KEY,
      JSON.stringify([...keys])
    );
  } catch {
    // ignore quota / privacy mode errors
  }
}

export function isFavoriteSlashItem(item: SlashMenuItem): boolean {
  return getFavoriteSlashKeys().has(favoriteKey(item));
}

/**
 * Toggles the favorite state of an item and returns the new state.
 */
export function toggleFavoriteSlashItem(item: SlashMenuItem): boolean {
  const keys = getFavoriteSlashKeys();
  const key = favoriteKey(item);
  let isFavorite: boolean;
  if (keys.has(key)) {
    keys.delete(key);
    isFavorite = false;
  } else {
    keys.add(key);
    isFavorite = true;
  }
  persistFavoriteSlashKeys(keys);
  return isFavorite;
}

export function formatDate(date: Date) {
  // yyyy-mm-dd
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0');
  const day = date.getDate().toString().padStart(2, '0');
  const strTime = `${year}-${month}-${day}`;
  return strTime;
}

export function formatTime(date: Date) {
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  const strTime = `${formatDate(date)} ${hours}:${minutes}`;
  return strTime;
}

/**
 * Maps a Cyrillic character (Russian ЙЦУКЕН layout) to the Latin character
 * produced by the same physical key on a QWERTY layout, so users typing
 * "in Russian" while on the English layout (e.g. `кан`) can still find
 * items by their Russian names.
 */
const CYRILLIC_TO_QWERTY: Record<string, string> = {
  й: 'q',
  ц: 'w',
  у: 'e',
  к: 'r',
  е: 't',
  н: 'y',
  г: 'u',
  ш: 'i',
  щ: 'o',
  з: 'p',
  х: '[',
  ф: 'a',
  ы: 's',
  в: 'd',
  а: 'f',
  п: 'g',
  р: 'h',
  о: 'j',
  л: 'k',
  д: 'l',
  ж: ';',
  я: 'z',
  ч: 'x',
  с: 'c',
  м: 'v',
  и: 'b',
  т: 'n',
  ь: 'm',
  б: '.',
  ю: '/',
};

/**
 * Standard Cyrillic-to-Latin transliteration, so a Russian query can also be
 * matched against Latin names and aliases (e.g. `канбан` -> `kanban`).
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'e',
  ж: 'zh',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'h',
  ц: 'ts',
  ч: 'ch',
  ш: 'sh',
  щ: 'shch',
  ъ: '',
  ы: 'y',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

/**
 * Expands a slash menu query into all matching variants: the lowercased
 * original, its ЙЦУКЕН→QWERTY layout mapping and its Cyrillic→Latin
 * transliteration. Matching runs against every variant, so Russian input
 * finds both Russian and English item names.
 */
export function normalizeQuery(query: string): string[] {
  const q = query.toLowerCase();
  const variants = new Set<string>([q]);
  let cyrToQwerty = '';
  let cyrToLatin = '';
  for (const ch of q) {
    const qw = CYRILLIC_TO_QWERTY[ch];
    if (qw !== undefined) {
      cyrToQwerty += qw;
      cyrToLatin += CYRILLIC_TO_LATIN[ch] ?? ch;
      continue;
    }
    cyrToLatin += ch;
  }
  if (cyrToQwerty) {
    variants.add(cyrToQwerty);
    if (cyrToLatin !== q) variants.add(cyrToLatin);
  }
  return [...variants];
}
